import { getProgressionDrillById } from '../data/progression/drills';
import { getLessonFlowForSkill } from '../data/progression/lessonFlows';
import { getProgressionSkillById, getProgressionSkillsForSport } from '../data/progression/skills';
import { getSkillTracksForSport } from '../data/progression/skillTracks';
import { LESSON_STAGE_KEYS, PROGRESSION_LEVELS } from './progressionConstants';
import { buildReviewToProgressionInput } from './reviewToProgression';

function levelIndex(level) {
  return PROGRESSION_LEVELS.indexOf(level);
}

function startCase(value) {
  const text = String(value || '').replace(/_/g, ' ').trim();
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
}

// Pass 77 REPAIR — resolve the EXACT canonical skill the evidence names, by id. The evidence contract already
// resolved each human/lesson signal to a stable canonical skillId (progressionEvidence SIGNAL_MAPPINGS /
// lesson targetSkillId). We NEVER fall back to "first skill in some track": if the id does not resolve to a
// legitimate canonical skill for this sport, we return null (the focus/recommendation survive on the input) — the
// canonical evidence winner must never silently become an unrelated skill.
function resolveEvidenceSkill(progressionInput, skills) {
  const skillId = progressionInput.skillFocusSkillId || '';
  if (!skillId) return null;
  const skill = getProgressionSkillById(skillId);
  if (!skill || skill.sport !== progressionInput.sport) return null;
  // must be a real skill for this sport's active skill set
  return skills.some((s) => s.skillId === skill.skillId) ? skill : null;
}

function findNextSkill(currentSkill, skills) {
  if (!currentSkill) return null;

  const direct = (currentSkill.nextSkillIds || [])
    .map((id) => skills.find((skill) => skill.skillId === id))
    .find(Boolean);
  if (direct) return direct;

  const currentLevelIdx = levelIndex(currentSkill.level);
  return skills.find(
    (skill) =>
      skill.trackId === currentSkill.trackId &&
      skill.category === currentSkill.category &&
      levelIndex(skill.level) > currentLevelIdx
  ) || null;
}

function expandLessonFlow(flow) {
  if (!flow) return null;

  return {
    ...flow,
    stages: LESSON_STAGE_KEYS.map((stageKey) => ({
      stage: stageKey,
      title: startCase(stageKey),
      drills: (flow.stages?.[stageKey] || [])
        .map((drillId) => getProgressionDrillById(drillId))
        .filter(Boolean),
    })),
  };
}

export function buildProgressionRecommendationFromInput(progressionInput = {}, options = {}) {
  if (!progressionInput?.sport) {
    return null;
  }

  // Pass 77 — ZERO authoritative evidence → NO current/next skill, NO active track, NO lesson flow. The engine
  // must never fabricate a default skill/recommendation. `skillFocus` is empty exactly when the canonical
  // contract found no evidence (progressionInput.hasEvidence === false).
  if (!progressionInput.skillFocus || progressionInput.hasEvidence === false) {
    return {
      ...progressionInput,
      activeTrack: null,
      currentSkill: null,
      nextSkill: null,
      lessonFlow: null,
      recommendedCategory: progressionInput.movementCategory || '',
      unlockedTrackIds: [],
    };
  }

  const tracks = getSkillTracksForSport(progressionInput.sport);
  const skills = getProgressionSkillsForSport(progressionInput.sport);

  // Resolve the EXACT canonical skill the evidence names. No unrelated first-in-track fallback (Pass 77 REPAIR §6).
  const currentSkill = resolveEvidenceSkill(progressionInput, skills);
  // The active track is the resolved skill's OWN track — not a level-forced core track. When the evidence skill
  // cannot be resolved to a canonical skill, we advertise no active track / next skill / flow (but keep the focus
  // and reason that already ride on progressionInput).
  const activeTrack = currentSkill
    ? tracks.find((track) => track.trackId === currentSkill.trackId) || null
    : null;
  const nextSkill = findNextSkill(currentSkill, skills);
  const lessonFlow = currentSkill
    ? (expandLessonFlow(getLessonFlowForSkill(nextSkill?.skillId)) ||
       expandLessonFlow(getLessonFlowForSkill(currentSkill?.skillId)))
    : null;

  return {
    ...progressionInput,
    activeTrack,
    currentSkill,
    nextSkill,
    lessonFlow,
    recommendedCategory: progressionInput.movementCategory,
    // branch unlock reflects the DEMONSTRATED level only (currentLevel); with an unknown level nothing unlocks
    // beyond the always-open core track.
    unlockedTrackIds: tracks
      .filter((track) => !track.branchFromLevel || levelIndex(progressionInput.currentLevel) >= levelIndex(track.branchFromLevel))
      .map((track) => track.trackId),
  };
}

export function buildProgressionRecommendation(videos = [], progressSummary = {}) {
  const progressionInput = buildReviewToProgressionInput(progressSummary, videos);
  return buildProgressionRecommendationFromInput(progressionInput, {});
}
